/* eslint-disable no-underscore-dangle */
import _ from 'lodash';
import React from 'react';
import { commitMutation, graphql } from 'react-relay';
import { ConnectionHandler } from 'relay-runtime';
import PropTypes from 'prop-types';
import { FloatingLabelInput as Input, SelectableBlock } from '../../Elements';
import {
  Analytics,
  DeleteConfirmation,
  DthcActions,
  GlobalNotifications,
  Intent,
  MobileModal,
  TextEditor
} from '../../Modules';
import { Button, Checkbox, Modal } from 'semantic-ui-react';
import nullifyEmptyStrings from '../../../lib/utils/nullifyEmptyStrings';
import paymentsHelper from '../../../lib/utils/payments';
import styles from './PaymentMethods.scss';
import styleVariables from '../../variables';
import Formsy from 'formsy-react';

export default class AcceptedPaymentMethods extends React.Component {
  static propTypes = {
    node: PropTypes.object,
    relay: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      editModalOpen: false,
      newModalOpen: false,
      squareModalOpen: false,
      submitted: false,
      isNew: false,
      newMethod: {
        enabled: false,
        instructions: '',
        label: ''
      }
    };
    this.onUpdateValidSubmit = this.onUpdateValidSubmit.bind(this);
    this.onUpdateInvalidSubmit = this.onUpdateInvalidSubmit.bind(this);
    this.onCreateValidSubmit = this.onCreateValidSubmit.bind(this);
    this.onCreateInvalidSubmit = this.onCreateInvalidSubmit.bind(this);
    this.onCheckboxChange = this.onCheckboxChange.bind(this);
    this.getPaymentMethod = this.getPaymentMethod.bind(this);
    this.openEditPaymentMethod = this.openEditPaymentMethod.bind(this);
    this.closeEditPaymentMethod = this.closeEditPaymentMethod.bind(this);
    this.openNewPaymentMethod = this.openNewPaymentMethod.bind(this);
    this.closeNewPaymentMethod = this.closeNewPaymentMethod.bind(this);
    this.textFieldChange = this.textFieldChange.bind(this);
    this.textEditorChange = this.textEditorChange.bind(this);
    this.newMethodTextFieldChange = this.newMethodTextFieldChange.bind(this);
    this.newMethodTextEditorChange = this.newMethodTextEditorChange.bind(this);
    this.createPaymentMethod = this.createPaymentMethod.bind(this);
    this.updatePaymentMethod = this.updatePaymentMethod.bind(this);
    this.onCreateNewValidSubmit = this.onCreateNewValidSubmit.bind(this);
    this.onCreateNewInvalidSubmit = this.onCreateNewInvalidSubmit.bind(this);
    this.updateWindowDimensions = this.updateWindowDimensions.bind(this);
    this.deleteCustomPaymentMethod = this.deleteCustomPaymentMethod.bind(this);
  }

  componentWillMount() {
    this.updateWindowDimensions();
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateWindowDimensions);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWindowDimensions);
  }

  onCreateValidSubmit() {
    this.setState({ submitted: true });
    this.createPaymentMethod(this.closeEditPaymentMethod);
  }

  onCreateInvalidSubmit() {
    this.setState({ submitted: true });
    GlobalNotifications.show({
      message: 'Unable to update payment method',
      intent: Intent.DANGER
    });
  }

  onCreateNewValidSubmit() {
    this.setState({ submitted: true });
    this.createCustomPaymentMethod(this.closeNewPaymentMethod);
  }

  onCreateNewInvalidSubmit() {
    this.setState({ submitted: true });
    GlobalNotifications.show({
      message: 'Unable to add payment method',
      intent: Intent.DANGER
    });
  }

  onUpdateValidSubmit() {
    this.setState({ submitted: true });
    this.updatePaymentMethod(this.closeEditPaymentMethod);
  }

  onUpdateInvalidSubmit() {
    this.setState({ submitted: true });
    GlobalNotifications.show({
      message: 'Unable to update payment method',
      intent: Intent.DANGER
    });
  }

  onCheckboxChange(e, data) {
    const state = _.cloneDeep(this.state);
    state.method[data.name] = data.checked;
    this.setState(state);
  }

  getPaymentMethod(typename) {
    const methods = this.props.viewer.payment_methods.edges.filter(
      method => method.node.__typename === typename
    );
    return methods && methods.length > 0 ? methods[0].node : null;
  }

  openEditPaymentMethod(method, isNew) {
    this.setState({ editModalOpen: true, method, isNew, submitted: false });
  }

  closeEditPaymentMethod() {
    this.setState({ editModalOpen: false });
  }

  openNewPaymentMethod() {
    this.setState({
      newModalOpen: true,
      newMethod: {
        enabled: false,
        instructions: '',
        label: ''
      },
      submitted: false
    });
  }

  closeNewPaymentMethod() {
    this.setState({ newModalOpen: false });
  }

  createCustomPaymentMethod(successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to add payment method',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      if (successCb) successCb();
      GlobalNotifications.show({
        message: 'Successfully added payment method',
        intent: Intent.SUCCESS
      });
      Analytics.trackEvent('created-custom-method');
    };
    const mutation = graphql`
      mutation AcceptedPaymentMethods_CreateCustomPaymentMethodMutation(
        $input: CreateCustomPaymentMethodInput!
      ) {
        createCustomPaymentMethod(input: $input) {
          custom_payment_method {
            __typename
            id
            enabled
            label
            instructions
          }
          invoice {
            ...InvoiceContainer_node
          }
          user {
            payment_methods {
              edges {
                node {
                  ... on CustomPaymentMethod {
                    __typename
                    id
                    instructions
                    label
                    enabled
                  }
                }
              }
            }
          }
        }
      }
    `;

    const customPaymentMethod = _.pick(
      nullifyEmptyStrings(this.state.newMethod),
      ['instructions', 'enabled', 'label']
    );
    if (this.props.node) {
      customPaymentMethod.invoice_id = this.props.node.id;
    }

    const environment = this.props.relay.environment;

    const updater = store => {
      const payload = store.getRootField('createCustomPaymentMethod');
      const node = payload.getLinkedRecord('custom_payment_method');
      const viewerProxy = store.get(this.props.viewer.id);
      const paymentMethods = viewerProxy.getLinkedRecord('payment_methods');
      const newEdge = ConnectionHandler.createEdge(
        store,
        paymentMethods,
        node,
        'PaymentMethodEdge'
      );
      const conn = ConnectionHandler.getConnection(
        viewerProxy,
        'SettingsContainer_payment_methods'
      );
      const invoiceConn = ConnectionHandler.getConnection(
        viewerProxy,
        'InvoiceContainer_payment_methods'
      );
      ConnectionHandler.insertEdgeAfter(conn, newEdge);
      ConnectionHandler.insertEdgeAfter(invoiceConn, newEdge);
    };

    commitMutation(environment, {
      mutation,
      updater,
      variables: {
        input: {
          ...customPaymentMethod,
          enabled: true,
          user_id: this.props.viewer.id
        }
      },
      onCompleted,
      onError
    });
  }

  createPaymentMethod(successCb, failureCb) {
    switch (this.state.method.__typename) {
      case 'CashPaymentMethod':
        this.createCashPaymentMethod(successCb, failureCb);
        break;
      case 'CheckPaymentMethod':
        this.createCheckPaymentMethod(successCb, failureCb);
        break;
      default:
        break;
    }
  }

  createCashPaymentMethod(successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to add payment method',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      if (successCb) successCb();
      GlobalNotifications.show({
        message: 'Successfully added payment method',
        intent: Intent.SUCCESS
      });
      Analytics.trackEvent('created-cash-payment-method');
    };
    const mutation = graphql`
      mutation AcceptedPaymentMethods_CreateCashPaymentMethodMutation(
        $input: CreateCashPaymentMethodInput!
      ) {
        createCashPaymentMethod(input: $input) {
          cash_payment_method {
            id
            enabled
            instructions
          }
          invoice {
            ...InvoiceContainer_node
          }
          user {
            payment_methods {
              edges {
                node {
                  ... on CashPaymentMethod {
                    __typename
                    id
                    instructions
                    enabled
                  }
                }
              }
            }
          }
        }
      }
    `;

    const cashPaymentMethod = _.pick(nullifyEmptyStrings(this.state.method), [
      'instructions',
      'enabled'
    ]);
    if (this.props.node) {
      cashPaymentMethod.invoice_id = this.props.node.id;
    }

    const environment = this.props.relay.environment;

    const updater = store => {
      const payload = store.getRootField('createCashPaymentMethod');
      const node = payload.getLinkedRecord('cash_payment_method');
      const viewerProxy = store.get(this.props.viewer.id);
      const paymentMethods = viewerProxy.getLinkedRecord('payment_methods');
      const newEdge = ConnectionHandler.createEdge(
        store,
        paymentMethods,
        node,
        'PaymentMethodEdge'
      );
      const conn = ConnectionHandler.getConnection(
        viewerProxy,
        'SettingsContainer_payment_methods'
      );
      const invoiceConn = ConnectionHandler.getConnection(
        viewerProxy,
        'InvoiceContainer_payment_methods'
      );
      ConnectionHandler.insertEdgeAfter(conn, newEdge);
      ConnectionHandler.insertEdgeAfter(invoiceConn, newEdge);
    };

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          ...cashPaymentMethod,
          user_id: this.props.viewer.id
        }
      },
      updater,
      onCompleted,
      onError
    });
  }

  createCheckPaymentMethod(successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to add payment method',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      if (successCb) successCb();
      GlobalNotifications.show({
        message: 'Successfully added payment method',
        intent: Intent.SUCCESS
      });
      Analytics.trackEvent('created-check-payment-method');
    };
    const mutation = graphql`
      mutation AcceptedPaymentMethods_CreateCheckPaymentMethodMutation(
        $input: CreateCheckPaymentMethodInput!
      ) {
        createCheckPaymentMethod(input: $input) {
          check_payment_method {
            id
            address
            enabled
            pay_to
          }
          invoice {
            ...InvoiceContainer_node
          }
          user {
            payment_methods {
              edges {
                node {
                  ... on CheckPaymentMethod {
                    __typename
                    id
                    address
                    enabled
                    pay_to
                  }
                }
              }
            }
          }
        }
      }
    `;

    const checkPaymentMethod = _.pick(nullifyEmptyStrings(this.state.method), [
      'enabled',
      'address',
      'pay_to'
    ]);
    if (this.props.node) {
      checkPaymentMethod.invoice_id = this.props.node.id;
    }

    const environment = this.props.relay.environment;

    const updater = store => {
      const payload = store.getRootField('createCheckPaymentMethod');
      const node = payload.getLinkedRecord('check_payment_method');
      const viewerProxy = store.get(this.props.viewer.id);
      const paymentMethods = viewerProxy.getLinkedRecord('payment_methods');
      const newEdge = ConnectionHandler.createEdge(
        store,
        paymentMethods,
        node,
        'PaymentMethodEdge'
      );
      const conn = ConnectionHandler.getConnection(
        viewerProxy,
        'SettingsContainer_payment_methods'
      );
      const invoiceConn = ConnectionHandler.getConnection(
        viewerProxy,
        'InvoiceContainer_payment_methods'
      );
      ConnectionHandler.insertEdgeAfter(conn, newEdge);
      ConnectionHandler.insertEdgeAfter(invoiceConn, newEdge);
    };

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          ...checkPaymentMethod,
          user_id: this.props.viewer.id
        }
      },
      updater,
      onCompleted,
      onError
    });
  }

  updatePaymentMethod(successCb, failureCb) {
    switch (this.state.method.__typename) {
      case 'CashPaymentMethod':
        this.updateCashPaymentMethod(successCb, failureCb);
        break;
      case 'CheckPaymentMethod':
        this.updateCheckPaymentMethod(successCb, failureCb);
        break;
      case 'SquarePaymentMethod':
        this.updateSquarePaymentMethod(successCb, failureCb);
        break;
      case 'CustomPaymentMethod':
        this.updateCustomPaymentMethod(successCb, failureCb);
        break;
      default:
        break;
    }
  }

  updateCashPaymentMethod(successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to update payment method',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      if (successCb) successCb();
      GlobalNotifications.show({
        message: 'Successfully updated payment method',
        intent: Intent.SUCCESS
      });
      Analytics.trackEvent('updated-cash-payment-method');
    };
    const mutation = graphql`
      mutation AcceptedPaymentMethods_UpdateCashPaymentMethodMutation(
        $input: UpdateCashPaymentMethodInput!
      ) {
        updateCashPaymentMethod(input: $input) {
          cash_payment_method {
            id
            enabled
            instructions
          }
          invoice {
            ...InvoiceContainer_node
          }
        }
      }
    `;

    const cashPaymentMethod = _.pick(nullifyEmptyStrings(this.state.method), [
      'id',
      'instructions',
      'enabled'
    ]);

    if (this.props.node) {
      cashPaymentMethod.invoice_id = this.props.node.id;
    }

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          ...cashPaymentMethod
        }
      },
      onCompleted,
      onError
    });
  }

  updateCustomPaymentMethod(successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to update payment method',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      if (successCb) successCb();
      GlobalNotifications.show({
        message: 'Successfully updated payment method',
        intent: Intent.SUCCESS
      });
      Analytics.trackEvent('updated-custom-method');
    };
    const mutation = graphql`
      mutation AcceptedPaymentMethods_UpdateCustomPaymentMethodMutation(
        $input: UpdateCustomPaymentMethodInput!
      ) {
        updateCustomPaymentMethod(input: $input) {
          custom_payment_method {
            id
            enabled
            label
            instructions
          }
          invoice {
            ...InvoiceContainer_node
          }
        }
      }
    `;

    const customPaymentMethod = _.pick(nullifyEmptyStrings(this.state.method), [
      'id',
      'instructions',
      'label',
      'enabled'
    ]);

    if (this.props.node) {
      customPaymentMethod.invoice_id = this.props.node.id;
    }

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          ...customPaymentMethod
        }
      },
      onCompleted,
      onError
    });
  }

  updateCheckPaymentMethod(successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to update payment method',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      if (successCb) successCb();
      GlobalNotifications.show({
        message: 'Successfully updated payment method',
        intent: Intent.SUCCESS
      });
      Analytics.trackEvent('updated-check-payment-method');
    };
    const mutation = graphql`
      mutation AcceptedPaymentMethods_UpdateCheckPaymentMethodMutation(
        $input: UpdateCheckPaymentMethodInput!
      ) {
        updateCheckPaymentMethod(input: $input) {
          check_payment_method {
            id
            address
            enabled
            pay_to
          }
          invoice {
            ...InvoiceContainer_node
          }
        }
      }
    `;

    const checkPaymentMethod = _.pick(nullifyEmptyStrings(this.state.method), [
      'id',
      'address',
      'pay_to',
      'enabled'
    ]);
    if (this.props.node) {
      checkPaymentMethod.invoice_id = this.props.node.id;
    }

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          ...checkPaymentMethod
        }
      },
      onCompleted,
      onError
    });
  }

  updateSquarePaymentMethod(successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to update payment method',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      if (successCb) successCb();
      GlobalNotifications.show({
        message: 'Successfully updated payment method',
        intent: Intent.SUCCESS
      });
      Analytics.trackEvent('updated-square-payment-method');
    };
    const mutation = graphql`
      mutation AcceptedPaymentMethods_UpdateSquarePaymentMethodMutation(
        $input: UpdateSquarePaymentMethodInput!
      ) {
        updateSquarePaymentMethod(input: $input) {
          square_payment_method {
            id
            enabled
          }
          invoice {
            ...InvoiceContainer_node
          }
        }
      }
    `;

    const squarePaymentMethod = _.pick(nullifyEmptyStrings(this.state.method), [
      'id',
      'enabled'
    ]);
    if (this.props.node) {
      squarePaymentMethod.invoice_id = this.props.node.id;
    }

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          ...squarePaymentMethod
        }
      },
      onCompleted,
      onError
    });
  }

  textFieldChange(e) {
    const state = _.cloneDeep(this.state);
    state.method[e.target.name] = e.target.value;
    this.setState(state);
  }

  textEditorChange(name, value) {
    const state = _.cloneDeep(this.state);
    state.method[name] = value;
    this.setState(state);
  }

  newMethodTextFieldChange(e) {
    const state = _.cloneDeep(this.state);
    state.newMethod[e.target.name] = e.target.value;
    this.setState(state);
  }

  newMethodTextEditorChange(name, value) {
    const state = _.cloneDeep(this.state);
    state.newMethod[name] = value;
    this.setState(state);
  }

  updateWindowDimensions() {
    this.setState({
      window: { width: window.innerWidth, height: window.innerHeight }
    });
  }

  deleteCustomPaymentMethod(id, successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to delete payment method',
        intent: Intent.DANGER
      });
    };

    const onCompleted = () => {
      if (successCb) successCb();
      GlobalNotifications.show({
        message: 'Successfully deleted payment method',
        intent: Intent.SUCCESS
      });
      Analytics.trackEvent('deleted-custom-payment-method');
    };

    const mutation = graphql`
      mutation AcceptedPaymentMethods_deleteCustomPaymentMethodMutation(
        $input: DeleteCustomPaymentMethodInput!
      ) {
        deleteCustomPaymentMethod(input: $input) {
          deletedCustomPaymentMethodId
        }
      }
    `;

    const environment = this.props.relay.environment;

    const updater = store => {
      const payload = store.getRootField('deleteCustomPaymentMethod');
      const deletedCustomPaymentMethodId = payload.getValue(
        'deletedCustomPaymentMethodId'
      );
      const viewerProxy = store.get(this.props.viewer.id);
      const conn = ConnectionHandler.getConnection(
        viewerProxy,
        'SettingsContainer_payment_methods'
      );
      const invoiceConn = ConnectionHandler.getConnection(
        viewerProxy,
        'InvoiceContainer_payment_methods'
      );
      ConnectionHandler.deleteNode(conn, deletedCustomPaymentMethodId);
      ConnectionHandler.deleteNode(invoiceConn, deletedCustomPaymentMethodId);
    };

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          id
        }
      },
      updater,
      onCompleted,
      onError
    });
  }

  render() {
    const tag = ({ enabled }) =>
      <span
        className={[
          styles.paymentMethodIndicator,
          enabled ? styles.paymentMethodEnabled : styles.paymentMethodDisabled
        ].join(' ')}
      >
        {enabled ? 'Enabled' : 'Disabled'}
      </span>;

    tag.propTypes = {
      enabled: PropTypes.bool
    };

    const squareConnectButton = (
      <Button
        primary
        onClick={() => {
          window.location = '/auth/square';
        }}
      >
        Continue
      </Button>
    );

    const squareInstructions = (
      <div>
        <h3>We&apos;ll first need to connect to your Square account</h3>
        <p>
          You will be redirected to Square to log into or create a new Square
          account
        </p>
      </div>
    );

    const EditPaymentMethodFields = (
      <div>
        {this.state.method &&
          <div style={{ display: 'flex' }}>
            <span style={{ flex: 1 }}>
              <h3>
                {this.state.method &&
                  this.state.method.__typename !== 'CustomPaymentMethod' &&
                  paymentsHelper.methods(this.state.method)}
                {this.state.method &&
                  this.state.method.__typename === 'CustomPaymentMethod' &&
                  <Input
                    label='Payment Method Name'
                    type='text'
                    name='label'
                    placeholder='(Eg. Paypal, Venmo, QuickPay)'
                    value={this.state.method.label || ''}
                    onChange={this.textFieldChange}
                    submitted={this.state.submitted}
                    fluid
                    required
                    validationError='Name is required'
                  />}
              </h3>
            </span>
            <div className={styles.paymentMethodToggle}>
              <Checkbox
                checked={this.state.method.enabled}
                label={this.state.method.enabled ? 'Enabled' : 'Disabled'}
                name='enabled'
                toggle
                onChange={this.onCheckboxChange}
              />
            </div>
          </div>}
        {this.state.method &&
          this.state.method.__typename === 'CashPaymentMethod' &&
          <div>
            <p>
              Please provide instructions so your client knows where to send the
              cash
            </p>
            <TextEditor
              type='text'
              name='instructions'
              placeholder={
                "Just throw it out the window. We'll come pick it up."
              }
              value={this.state.method.instructions || ''}
              onChange={(value) => this.textEditorChange('instructions', value)}
              submitted={this.state.submitted}
              required
              validationError='Instructions are required'
            />
          </div>}
        {this.state.method &&
          this.state.method.__typename === 'CustomPaymentMethod' &&
          <div>
            <p>Please provide instructions so your client knows how to pay</p>
            <TextEditor
              placeholder='(E.g. Send all payments to Venmo username @cloverbook)'
              value={this.state.method.instructions || ''}
              onChange={(value) => this.textEditorChange('instructions', value)}
            />
          </div>}
        {this.state.method &&
          this.state.method.__typename === 'CheckPaymentMethod' &&
          <div>
            <p>
              Please provide instructions so your client know how to write the
              check and where to send it
            </p>
            <Input
              label='Pay To The Order Of'
              type='text'
              name='pay_to'
              placeholder='Your Company, Inc.'
              submitted={this.state.submitted}
              required
              validationError='Name is required'
              value={this.state.method.pay_to || ''}
              onChange={this.textFieldChange}
            />
            <Input
              label='Mailing Address'
              type='text'
              name='address'
              placeholder={`1540 Market Street
San Francisco, CA 94102`}
              autoHeight
              rows={2}
              submitted={this.state.submitted}
              value={this.state.method.address || ''}
              required
              validationError='Mailing address is required'
              onChange={this.textFieldChange}
            />
          </div>}
      </div>
    );

    const NewPaymentMethodFields = (
      <Formsy.Form
        ref='newForm'
        onValidSubmit={this.onCreateNewValidSubmit}
        onInvalidSubmit={this.onCreateNewInvalidSubmit}
      >
        <h3>New Payment Method</h3>
        <div>
          <Input
            label='Payment Method Name'
            type='text'
            name='label'
            placeholder='(Eg. Paypal, Venmo, QuickPay)'
            autoHeight
            value={this.state.newMethod.label || ''}
            onChange={this.newMethodTextFieldChange}
            submitted={this.state.submitted}
            required
            validationError='Payment method name is required'
          />
          <p>Please provide instructions so your client knows how to pay you</p>
          <TextEditor
            placeholder='(E.g. Send all payments to Venmo username @cloverbook)'
            value={this.state.newMethod.instructions || ''}
            onChange={(value) => this.newMethodTextEditorChange('instructions', value)}
          />
        </div>
      </Formsy.Form>
    );

    const deleteButton = (
      <Button negative fluid>
        Delete Payment Method
      </Button>
    );

    return (
      <div>
        {this.state.window.width < styleVariables.tabletBreakpoint &&
          <div>
            <MobileModal
              open={this.state.squareModalOpen}
              onClose={() => this.setState({ squareModalOpen: false })}
            >
              <MobileModal.Header>
                <div className='menu'>
                  <a
                    className='menu item'
                    onClick={() => this.setState({ squareModalOpen: false })}
                  >
                    Cancel
                  </a>
                </div>
              </MobileModal.Header>
              <MobileModal.Content>
                {squareInstructions}
                <br />
                {squareConnectButton}
              </MobileModal.Content>
            </MobileModal>
            <MobileModal
              open={this.state.newModalOpen}
              onClose={this.closeNewPaymentMethod}
            >
              <MobileModal.Header>
                <div className='menu'>
                  <a className='item' onClick={this.closeNewPaymentMethod}>
                    Cancel
                  </a>
                </div>
                <div className='right menu'>
                  <a
                    className='item'
                    onClick={() => this.refs.newForm.submit()}
                  >
                    Save
                  </a>
                </div>
              </MobileModal.Header>
              <MobileModal.Content>
                {NewPaymentMethodFields}
              </MobileModal.Content>
            </MobileModal>
          </div>}
        {this.state.window.width >= styleVariables.tabletBreakpoint &&
          <div>
            <Modal
              open={this.state.squareModalOpen}
              size='small'
              onClose={() => this.setState({ squareModalOpen: false })}
            >
              <Modal.Header content='Connect to Square' />
              <Modal.Content>
                {squareInstructions}
              </Modal.Content>
              <Modal.Actions>
                <Button
                  basic
                  onClick={() => this.setState({ squareModalOpen: false })}
                >
                  Cancel
                </Button>
                {squareConnectButton}
              </Modal.Actions>
            </Modal>
            <Modal
              open={this.state.newModalOpen}
              onClose={this.closeNewPaymentMethod}
              size='small'
            >
              <Modal.Header content='Add New Payment Method' />
              <Modal.Content>
                {NewPaymentMethodFields}
              </Modal.Content>
              <Modal.Actions>
                <DthcActions
                  reset={this.closeNewPaymentMethod}
                  save={() => this.refs.newForm.submit()}
                />
              </Modal.Actions>
            </Modal>
          </div>}
        <Formsy.Form
          ref='form'
          key={this.state.method && this.state.method.id}
          onValidSubmit={
            this.state.isNew
              ? this.onCreateValidSubmit
              : this.onUpdateValidSubmit
          }
          onInvalidSubmit={
            this.state.isNew
              ? this.onCreateInvalidSubmit
              : this.onUpdateInvalidSubmit
          }
        >
          {this.state.window.width < styleVariables.tabletBreakpoint &&
            <MobileModal
              open={this.state.editModalOpen}
              onClose={this.closeEditPaymentMethod}
            >
              <MobileModal.Header>
                <div className='menu'>
                  <a className='item' onClick={this.closeEditPaymentMethod}>
                    Cancel
                  </a>
                </div>
                <div className='right menu'>
                  <a className='item' onClick={() => this.refs.form.submit()}>
                    Save
                  </a>
                </div>
              </MobileModal.Header>
              <MobileModal.Content>
                {EditPaymentMethodFields}
                {this.state.method &&
                  this.state.method.__typename === 'CustomPaymentMethod' &&
                  <DeleteConfirmation
                    trigger={deleteButton}
                    delete={() =>
                      this.deleteCustomPaymentMethod(
                        this.state.method.id,
                        this.closeEditPaymentMethod
                      )}
                    id={this.state.method.id}
                  />}
              </MobileModal.Content>
            </MobileModal>}
          {this.state.window.width >= styleVariables.tabletBreakpoint &&
            <Modal
              open={this.state.editModalOpen}
              onClose={this.closeEditPaymentMethod}
              size='small'
            >
              <Modal.Header content='Edit Payment Method' />
              <Modal.Content>
                {EditPaymentMethodFields}
              </Modal.Content>
              <Modal.Actions>
                <DthcActions
                  delete={
                    this.state.method &&
                    this.state.method.__typename === 'CustomPaymentMethod'
                      ? () =>
                          this.deleteCustomPaymentMethod(
                            this.state.method.id,
                            this.closeEditPaymentMethod
                          )
                      : null
                  }
                  reset={this.closeEditPaymentMethod}
                  save={() => this.refs.form.submit()}
                />
              </Modal.Actions>
            </Modal>}
        </Formsy.Form>
        {this.props.viewer.payment_methods.edges.map(method =>
          <SelectableBlock
            key={method.node.id}
            checked={method.enabled}
            style={{ marginBottom: '2em' }}
            onChange={this.openEditPaymentMethod.bind(null, method.node, false)}
          >
            <h4>
              {paymentsHelper.methods(method.node)}
              {tag({ enabled: method.node.enabled })}
            </h4>
            {method.node.enabled &&
              method.node.__typename === 'CashPaymentMethod' &&
              <div dangerouslySetInnerHTML={{ __html: method.node.instructions }} />}
            {method.node.enabled &&
              method.node.__typename === 'CustomPaymentMethod' &&
              <div dangerouslySetInnerHTML={{ __html: method.node.instructions }} />}
            {method.node.enabled &&
              method.node.__typename === 'CheckPaymentMethod' &&
              <div>
                <div>
                  <em>
                    Pay to the order of: {method.node.pay_to}
                  </em>
                </div>
                <div style={{ whiteSpace: 'pre' }}>
                  {method.node.address}
                </div>
              </div>}
          </SelectableBlock>
        )}
        {!this.getPaymentMethod('CashPaymentMethod') &&
          <SelectableBlock
            checked={false}
            style={{ marginBottom: '2em' }}
            onChange={this.openEditPaymentMethod.bind(
              null,
              { __typename: 'CashPaymentMethod', enabled: false },
              true
            )}
          >
            <h4>
              Cash
              {tag({ enabled: false })}
            </h4>
          </SelectableBlock>}
        {!this.getPaymentMethod('CheckPaymentMethod') &&
          <SelectableBlock
            checked={false}
            style={{ marginBottom: '2em' }}
            onChange={this.openEditPaymentMethod.bind(
              null,
              { __typename: 'CheckPaymentMethod', enabled: false },
              true
            )}
          >
            <h4>
              Check
              {tag({ enabled: false })}
            </h4>
          </SelectableBlock>}
        {!this.getPaymentMethod('SquarePaymentMethod') &&
          <SelectableBlock
            checked={false}
            style={{ marginBottom: '2em' }}
            onChange={() => this.setState({ squareModalOpen: true })}
          >
            <h4>
              Credit Card
              {tag({ enabled: false })}
            </h4>
          </SelectableBlock>}
        <a onClick={this.openNewPaymentMethod}>+ Add New</a>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./webpack/components/App/PaymentMethods/AcceptedPaymentMethods.js